import React from 'react';
import { observer } from 'mobx-react-lite';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Pencil1Icon, PlusIcon } from '@radix-ui/react-icons';
import { IconButton } from '@producer/ui-kit';

import { formatDate } from '../../../lib/utils/DateHelper';
import { dateShort } from '../../../assets/contants/dates-fns';
import { timeTo12HourFormat } from '../../../lib/utils/Convertor';
import { Tooltip } from '../../../components/tooltip/Tooltip';
import { InlineTimeSelector } from '../../schedule/components/InlineTimeSelector';

import shared from '../stripboard.module.scss';
import styles from './day-start-strip.module.scss';

export type DayStartStripProps = {
  dayId: string;
  dayName: string;
  dodText: string;
  startDate?: string;
  generalCallTime?: { hour: number; minute: number };
  onChangeTime?: (value: { hour: number; minute: number }) => void;
  onRenameClick?: () => void;
  onAddShot?: () => void;
};

const DayStartStripComponent: React.FC<DayStartStripProps> = observer(
  ({
    dayId,
    dayName,
    dodText,
    startDate,
    onChangeTime,
    onRenameClick,
    onAddShot,
    generalCallTime,
  }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
      id: `${dayId}-start-strip`,
      data: {
        dayId,
        type: 'row',
      },
      disabled: true,
      animateLayoutChanges: () => true,
      transition: {
        duration: 150,
        easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
      },
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    const formattedDate = startDate ? formatDate(startDate, dateShort) : '';

    return (
      <div
        className={shared.stickyStrip}
        data-direction="top"
        style={style}
        ref={setNodeRef}
        {...attributes}
        {...listeners}
      >
        <div className={shared.stickyStripContent}>
          <div className={styles['name-wrapper']}>
            <h3 className={styles['day-name']}>{dayName}</h3>

            <IconButton
              icon={<Pencil1Icon color="var(--colors-texts-high-contrast)" />}
              onClick={onRenameClick}
            />
          </div>

          <div className={styles['center-wrapper']}>
            <div className={styles['left-wrapper']}>{formattedDate && <p className={styles['day-text']}>{formattedDate}</p>}</div>

            {generalCallTime && (
              <InlineTimeSelector
                time={timeTo12HourFormat(generalCallTime)}
                textToDisplay="General call time "
                backgroundVisible
                style={{ margin: '0' }}
                onChange={onChangeTime}
              />
            )}

            <div className={styles['right-wrapper']}>
              <p className={`${styles['day-text']} ${styles['dod-text']}`}>{dodText}</p>
            </div>
          </div>

          <div className={shared.addShotButton}>
            <Tooltip text="Add shot">
              <IconButton
                icon={<PlusIcon />}
                onClick={onAddShot}
              />
            </Tooltip>
          </div>
        </div>
      </div>
    );
  },
);

export const DayStartStrip = React.memo(DayStartStripComponent);

